import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { DbNode, INode, isDbNode } from '../types/node';
import { NodeService } from '../http/node-service';
import { ILocation, Point } from '../types/location';
import { PositionService } from '../http/position-service';
import axios, { AxiosError } from 'axios';

export default class AdminNodeStore {
    private _nodeId: number;
    private _loading = false;
    private _items: DbNode[] = [];
    private _location: ILocation | null = null;

    constructor(nodeId: number = 0) {
        this._nodeId = nodeId;
        makeObservable<AdminNodeStore, '_loading' | '_items' | '_location'>(this, {
            _loading: observable,
            _items: observable,
            _location: observable,
            loading: computed,
            items: computed,
            load: action,
            loadNode: action,
            loadLocation: action,
        });
    }

    get loading(): boolean {
        return this._loading;
    }

    get items(): DbNode[] {
        return this._items;
    }

    get location(): ILocation | null {
        return this._location;
    }

    async load() {
        await this.loadNode();
        await this.loadLocation();
    }

    async loadNode() {
        runInAction(() => {
            this._loading = true;
        });
        try {
            const answer = await NodeService.getNodes(this._nodeId);
            runInAction(() => {
                this._items = answer.data;
            });
        } catch (e) {
            throw e;
        } finally {
            runInAction(() => {
                this._loading = false;
            });
        }
    }

    async loadLocation() {
        runInAction(() => {
            this._loading = true;
        });
        try {
            const answer = await PositionService.getByNode(this._nodeId);
            runInAction(() => {
                this._location = answer.data;
            });
        } catch (e) {
            if (axios.isAxiosError(e)) {
                const axError = e as AxiosError;
                if (axError.response?.status === 400) {
                    runInAction(() => {
                        this._location = null;
                    });
                } else {
                    throw e;
                }
            } else {
                throw e;
            }
        } finally {
            runInAction(() => {
                this._loading = false;
            });
        }
    }

    async updateNode(node: DbNode, address = '', coordinates: Point | null = null, locationChanged = false) {
        await NodeService.edit(node);

        if (locationChanged) {
            try {
                await PositionService.deleteByNode(node.node_id);
            } catch {
                // no existing location — that's fine
            }

            if (address !== '' && coordinates) {
                await PositionService.create({
                    node_id: node.node_id,
                    name: node.name,
                    description: null,
                    address,
                    coordinates,
                });
            }
        }
    }

    async createNode(node: INode, address = '', coordinates: Point | null = null) {
        try {
            const createdNodeAnswer = await NodeService.create({ ...node, parent_id: this._nodeId });
            const createdNode = createdNodeAnswer.data;
            if (!isDbNode(createdNode)) {
                throw new Error('Unexpected response: not a DbNode');
            }

            if (address !== '' && coordinates) {
                await PositionService.create({
                    node_id: createdNode.node_id,
                    name: createdNode.name,
                    description: null,
                    address: address,
                    coordinates: coordinates,
                });
            }
            this.load();
        } catch (e) {
            throw e;
        }
    }
}
